@file:OptIn(ExperimentalSerializationApi::class, ExperimentalTime::class, ExperimentalTime::class)

package net.sergeych.ugdx

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import net.sergeych.crypto2.SymmetricKey
import net.sergeych.crypto2.UniversalPrivateKey
import net.sergeych.crypto2.UniversalRing
import net.sergeych.kilogin.biEncode
import net.sergeych.kilogin.client.ClientSecretData
import net.sergeych.kilogin.decodeBipack
import net.sergeych.kiloparsec.RemoteInterface
import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_logger.warning
import net.sergeych.ugdx.realtime.RealtimeChannel
import net.sergeych.ugdx.shared.*
import net.sergeych.ugdx.shared.api.*
import kotlin.time.Duration.Companion.days
import kotlin.time.ExperimentalTime

data class UserAccount(
    internal var accountInfo: AccountInfo,
) : LogTag("UACC") {

    override fun equals(other: Any?): Boolean = other is UserAccount && other.accountInfo.id == accountInfo.id

    override fun hashCode(): Int {
        return accountInfo.id.hashCode()
    }

    @Suppress("unused")
    val isAdmin by accountInfo::isAdmin

    @Suppress("unused")
    val isBlocked by accountInfo::isBlocked


    @Suppress("unused")
    var canUseExchange: Boolean get() = accountInfo.canUseExchange
        internal set(value) {
            accountInfo.canUseExchange = value
        }

    val loginName by accountInfo::loginName
    val userId by accountInfo::id

    /**
     * Adapter to perfprm ethereum-specific operations with this account
     */
    val ethereum: EthereumAdapter by lazy { EthereumAdapter(this) }

    /**
     * @return true if the password has been recently entered and password-related operations such as [setData],
     * [getData] and other will most likely not cause system to request the password
     */
    @Suppress("unused")
    suspend fun hasDecryptedKeys() = GuestAPI().client.isLoggedIn

    suspend fun signOut() {
        GuestAPI().signOut()
    }

//    suspend fun passwordKeys(): PasswordKeys {
//        return rememberedPasswordKeys ?: run {
//            val request = object : ServiceState.PasswordRequest(), Loggable by LogTag("PSWIN") {
//                val lastState = net.sergeych.ugdx.GuestAPI().stateFlow.value
//
//                val channel = Channel<PasswordKeys>(2)
//
//                override suspend fun tryPassword(password: String): PasswordErrorResult? {
//                    debug { "start deriving password keys" }
//                    val pk = PasswordKeys.deriveFrom(password, accountInfo.derivationParams)
//                    debug { "password keys ready, we will try encrypted keyring of ${accountInfo.encryptedKeyring.size} bytes" }
//                    return try {
//                        // if we can decrypt it, we're OK and can use keyring too,
//                        // if not decryption fails prior to update keyring:
//                        _keyring = pk.decryptKeyring(accountInfo.encryptedKeyring)
//                        // fine, lets forward it to waiting co:
//                        debug { "keyring decrypted, sending it back to channel" }
//                        channel.send(pk)
//                        // and restore service state
//                        debug { "restoring last state of ${lastState}" }
//                        net.sergeych.ugdx.GuestAPI().states.value = lastState
//                        null
//                    } catch (x: Throwable) {
//                        // this means the password is wrong
//                        warning { "key decryption failed: $x" }
//                        PasswordErrorResult("wrong password", null)
//                    }
//                }
//
//                override fun cancel() {
//                    channel.close()
//                }
//            }
//
//            net.sergeych.ugdx.GuestAPI().states.value = request
//
//            debug { "waiting for password keys" }
//            return try {
//                request.channel.receive().also {
//                    rememberedPasswordKeys = it
//                    debug { "received password keys" }
//                }
//            } catch (x: ClosedReceiveChannelException) {
//                debug { "failed to get keys: channel cancelled (cancel selected?)" }
//                throw OperationCancelled()
//            }
//        }
//    }

    suspend fun myParty(): Party {
        val k = mainKey()
        return Party(partyId, loginName, k.publicKey)
    }

    suspend fun createInvoice(
        title: String,
        account: ApiAccount,
        issuedTo: Party,
        currency: Currency,
        items: List<Invoice.Item>,
        extras: List<Invoice.ExtraPayment> = listOf(),
        issuedAt: Instant = Clock.System.now(),
        dueBy: Instant = issuedAt + 15.days,
    ): SignedInvoice {
        val invoice = Invoice.create(
            title, partyId, account.id, issuedTo.id, currency.code,
            items, extras, dueBy = dueBy
        )
        return cmdInvoiceSubmit(SignedInvoice.sign(mainKey().signingKey, invoice))
    }

    /**
     * Perform test import. Important! If the realm of the currency is not TEST, the command most likely
     * will fail
     */
    suspend fun testImport(currency: Currency, amount: BigDecimal) {
        cmdTestImport(ApiTestImportArgs(currency.code, amount))
    }

    /**
     * Return actual list of all accounts of the application. The returned object will create all necessary
     * functions to navigate it.
     */
    suspend fun accounts(): GetAccountsResult = cmdGetAccounts.invoke()

    /**
     * Return existing default account for currency or null. Default accoutn logic is following:
     * - if there is accoutn with `isDefault` flag set, return it
     * - otherwise, the least recent (created first) account for the currency.
     */
    suspend fun defaultAccountFor(currency: Currency): ApiAccount? =
        accounts().accounts.filter { it.currencyCode == currency.code }
            .sortedWith { a, b ->
                if (a.isDefault == b.isDefault)
                    a.id.compareTo(b.id)
                else
                    if (a.isDefault) -1 else 1
            }
            .firstOrNull()


    suspend fun deleteAccount(account: ApiAccount) = cmdDeleteAccount(AccountIdArgs(account.id))

    suspend fun createAccount(currency: Currency, name: String): ApiAccount =
        cmdCreateAccount(CreateAccountArgs(currency.code, name))

    /**
     * Update account parameters
     */
    suspend fun updateAccount(account: ApiAccount, name: String, isDefault: Boolean, isBrokerage: Boolean = false) =
        cmdUpdateAccount(UpdateAccountArgs(account.id, isDefault, isBrokerage, name))

    suspend fun transferToMyAccount(from: ApiAccount, to: ApiAccount, amount: BigDecimal) {
        cmdTransferToMyAccount(LocalTransferArgs(from.id, to.id, amount))
    }

    suspend fun refreshAccount(account: ApiAccount): ApiAccount =
        cmdGetAccounts().accounts.find { it.id == account.id }
            ?: ErrorCode.NOT_FOUND.raise("account does not exist anymore")


    /**
     * Creates order to transfer funds to other user by the loginName. To perform transfer, order should be
     * reviewed by the user and accepted (then the library will sign it asking password if need), and sent
     * to the service for execution.
     */
    @Suppress("unused")
    fun createTransferOrder(from: ApiAccount, amount: BigDecimal, toLoginName: String, purpose: String): Order =
        LocalTransferOrder(from.id, amount, purpose, toLoginName)

    @Suppress("unused")
    suspend fun getOrder(orderId: Long): OrderRecord = cmdGetOrder(OrderIdArgs(orderId))

    /**
     * Get list of most recent orders (sorted most recent first)
     * @param earlierThanId only include orders submitted before given id
     * @param size do not include more records than this size.
     * @return list of orders matching the condition. Check [OrderListResult.hasMore] to see if there are matching
     *         orders earlier than the last one in the list.
     */
    @Suppress("unused")
    suspend fun orders(earlierThanId: String?, size: Int = 50): OrderListResult =
        cmdListOrders(OrderListArgs(earlierThanId, size))

    private var _keyring: UniversalRing? = null

    private suspend fun keyring(): UniversalRing {
        // TODO: add some more protection
        _keyring?.let { return it }
        return GuestAPI().client.loginDataOrThrow().let { d ->
            UniversalRing(d.dataKey to "data") +
                    (d.clientPrivateKey to "login") +
                    (d.packedSecretData!!.decodeBipack<UniversalPrivateKey>() to "main")
        }.also { _keyring = it }
    }

    suspend fun dataKey(): SymmetricKey {
        println(":key ring keys: ${keyring()}")
        return keyring().findByTags("data").first() as SymmetricKey
    }

    val partyId = accountInfo.partyId

    suspend fun mainKey(): UniversalPrivateKey =
        keyring().findByTags("main").first() as UniversalPrivateKey


    /**
     * Store arbitrary user data  on server using end-to-end encryption, e.g. server can't access the data it stores.
     * The key to store this type of data is also derived from the password, independently from other system keys.
     * Limitation on number of object and their size may apply. Use [getData] to load and [dropData] to remove stored
     * data.
     *
     * Supported value types:
     *
     * - String, Int, Long, Boolean, DataArray, Instant (kotlinx.datetime.*)
     * - Anything `@Serializable`
     */
    suspend inline fun <reified T> setData(key: String, value: T) {
        cmdUserSetData(UserSetDataArgs(key, dataKey().encrypt(biEncode(value))))
    }

    /**
     * Get user data, see [setData] for details.
     * @return restored data or null if no data is found.
     */
    suspend inline fun <reified T : Any> getData(key: String): T? =
        try {
            cmdUserGetData.invoke(UserGetDataArgs(key))
                .encryptedData.let { dataKey().decrypt(it).decodeBipack<T>() }
        } catch (x: RemoteInterface.RemoteException) {
            if (x.code.errorCode == ErrorCode.NOT_FOUND)
                null
            else throw x
        }

    private val partyCache = mutableMapOf<String, Party>()
    private val partyLoginCache = mutableMapOf<String, Party>()
    private val partyCacheAccess = Mutex()

    /**
     * Get the party by id using cache. Use [Party.refresh] when appropriate.
     */
    suspend fun getParty(partyId: String, includeKey: Boolean = true): Party? = try {
        partyCacheAccess.withLock {
            partyCache.getOrPut(partyId, { cmdPartyGet(PartyIdArgs(partyId, includeKey)) })
        }
    } catch (x: ApiException) {
        if (x.errorCode == ErrorCode.NOT_FOUND) null
        else throw x
    }

    suspend fun getPartyByLoginName(loginName: String, includeKey: Boolean = true): Party? = try {
        partyCacheAccess.withLock {
            partyLoginCache.getOrPut(loginName, { cmdPartyGetByLogin(PartyIdArgs(loginName, includeKey)) })
        }
    } catch (x: ApiException) {
        if (x.errorCode == ErrorCode.NOT_FOUND) null
        else throw x
    }

    @Serializable
    data class PackedList<T>(val list: List<T>)

    suspend inline fun <reified T> setDataList(key: String, value: List<T>) {
        cmdUserSetData(UserSetDataArgs(key, dataKey().encrypt(biEncode(PackedList(value)))))
    }

    suspend inline fun <reified T : Any> getDataList(key: String): List<T> =
        try {
            cmdUserGetData(UserGetDataArgs(key)).encryptedData.let {
                dataKey().decrypt(it).decodeBipack<PackedList<T>>().list
            }
        } catch (x: RemoteInterface.RemoteException) {
            println("--> ${x.code} --> ${x.message}")
            if (x.code.errorCode == ErrorCode.NOT_FOUND)
                listOf()
            else {
                x.printStackTrace()
                throw x
            }
        } catch (t: Throwable) {
            println("unexpected expcetion in getDataList:")
            t.printStackTrace()
            throw t
        }


    /**
     * Attempts to open real time channel that flows [PushPayload.AccountChanged] events whenever account
     * is changed, for example, balance is changed. It can block until the websocket or any other realtime
     * channel will be established and subscription command performed (if not yet subscribed).
     */
    suspend fun accountsFlow(): Flow<PushPayload.AccountChanged> = RealtimeChannel.accountFlow()

    /**
     * Drop user data created with [setData]. Does nothing if no such data exists.
     */
    suspend fun dropData(key: String) {
        cmdUserDropData(UserGetDataArgs(key))
    }

    fun clearPasswords() {
        // TODO: implement with ephemeral passwords
        warning { "at the moment this function could not be implemented" }
    }
}

val ClientSecretData<AccountInfo>.account: UserAccount get() = UserAccount(this.loginData!!)